import { PLUGIN_VERSION } from "@/helpers/plugin";

export interface SettingConfig {
  key: string;
  label: string;
  description?: string;
  category?: string;
  guideline?: string;
}

export interface CategoryLearnMore {
  text: string;
  url: string;
  linkText: string;
}

export const CATEGORIES = {
  READABILITY: "Readability",
  KEYBOARD_NAVIGATION: "Keyboard Navigation",
  ACCESSIBLE_NAME: "Accessible Names",
  RESPONSIVE_DESIGN: "Responsive Design",
  PERFORMANCE: "Performance & SEO",
  LINK_VISIBILITY: "Link Visibility",
  LINK_BEHAVIOR: "Link Behavior",
  FORM_LABELS: "Forms",
  IMAGE_ACCESSIBILITY: "Image Accessibility",
  MEDIA_ACCESSIBILITY: "Media Accessibility",
};

export const CATEGORY_LEARN_MORE: Record<string, CategoryLearnMore> = {
  [CATEGORIES.READABILITY]: {
    text: "Learn how lang and dir attributes fixes readability",
    url: `https://www.webyes.com/documentation/how-to-add-dir-and-lang-attributes?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "how lang and dir attributes fixes readability"
  },
  [CATEGORIES.KEYBOARD_NAVIGATION]: {
    text: "Enable these options on to have accessibility fixes applied automatically to your markup. ",
    url: `https://www.webyes.com/documentation/how-keyboard-navigation-enhances-web-accessibility?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "accessibility fixes"
  },
  [CATEGORIES.ACCESSIBLE_NAME]: {
    text: "See our guide on Why Managing Accessible Names Is Important",
    url: `https://www.webyes.com/documentation/managing-accessible-name?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "Why Managing Accessible Names Is Important"
  },
  [CATEGORIES.RESPONSIVE_DESIGN]: {
    text: "Ensure you have the fundamentals of mobile responsiveness implemented.",
    url: `https://www.webyes.com/documentation/responsive-design-accessibility?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "fundamentals of mobile responsiveness implemented"
  },
  [CATEGORIES.LINK_VISIBILITY]: {
    text: "Remove color independence from links and ensure proper link visibility.",
    url: `https://www.webyes.com/documentation/link-visibility-in-accessibility?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "link visibility"
  },
  [CATEGORIES.LINK_BEHAVIOR]: {
    text: "Remove uncertainty and avoid confusion with predictable link behavior.",
    url: `https://www.webyes.com/documentation/link-behaviour-accessibility?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "predictable link behavior"
  },
  [CATEGORIES.FORM_LABELS]: {
    text: "Understand the fundamentals of making forms accessible.",
    url: `https://www.webyes.com/documentation/proper-form-labelling-accessibility?utm_source=IssueFixes&utm_medium=AccessibilityPlus&utm_campaign=Documentation&utm_content=${PLUGIN_VERSION}`,
    linkText: "fundamentals of making forms accessible"
  },
};

export const SETTINGS_CONFIG: SettingConfig[] = [
  {
    key: "langDir.status",
    label: "Add lang and dir attributes to <html>",
    description: "Helps screen readers detect language and text direction for accurate reading.",
    category: CATEGORIES.READABILITY,
    guideline: "3.1.1",
  },
  {
    key: "removeTabIndex.status",
    label: "Remove tabindex from focusable elements",
    description:"Prevents custom tab order from breaking natural keyboard navigation.",
    category: CATEGORIES.KEYBOARD_NAVIGATION,
    guideline: "2.4.3",
  },
  {
    key: "removeTitleIfAccessibleName.status",
    label: "Remove title attributes when accessible names exist",
    description:
      "Avoids redundancy and confusion from overused or ignored title attributes",
    category: CATEGORIES.ACCESSIBLE_NAME,
    guideline: "2.4.4, 1.1.1",
  },    
  {
    key: "forceUnderlineOnLinks.status",
    label: "Underline non-navigation links",
    description:
      "Makes links clearly visible without relying on color alone.",
    category: CATEGORIES.LINK_VISIBILITY,
    guideline: "1.4.1",
  },
  {
    key: "metaViewportScale.status",
    label: "Ensure viewport allows scaling",
    description: "Lets users zoom on mobile for better readability and accessibility.",
    category: CATEGORIES.RESPONSIVE_DESIGN,
    guideline: "1.4.4, 1.4.10",
  },
  {
    key: "removeTargetBlank.status",
    label: 'Remove target="_blank" by default',
    description: 'Prevents unexpected new tabs or windows that can disorient users.',
    category: CATEGORIES.LINK_BEHAVIOR,
    guideline: "3.2.5",
  },
  {
    key: "addTargetBlankLabel.status",
    label: 'Add label/icon for target="_blank" links',
    description: "Informs users when a link opens in a new tab or window.",
    category: CATEGORIES.LINK_BEHAVIOR,
    guideline: "3.2.5",
  },
  {
    key: "enableSkipLink.status",
    label: "Add skip link to content",
    description: "Lets keyboard users jump directly to the main content area.",
    category: CATEGORIES.KEYBOARD_NAVIGATION,
    guideline: "2.4.1",
  },
  {
    key: "addMissingCommentFormLabels.status",
    label: "Add missing labels to WordPress comment form",
    description: "Ensures all form fields are properly labeled for screen readers.",
    category: CATEGORIES.FORM_LABELS,
    guideline: "3.3.2",
  },
  {
    key: "addMissingSearchFormLabels.status",
    label: "Add missing labels to WordPress search form",
    description: "Adds a descriptive label to the search field for accessibility.",
    category: CATEGORIES.FORM_LABELS,
    guideline: "1.3.1, 3.3.2",
  },
  {
    key: "addFocusOutline.status",
    label: "Add outline to focused elements",
    description: "Makes it easier to see which element is focused during keyboard use.",
    category: CATEGORIES.KEYBOARD_NAVIGATION,
    guideline: "2.4.7",
  }
];
